// src/pages/item/ItemScreen.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { useItemScreen } from "./useItemScreen";
import { useSwipeNavigation } from "../../common/hooks/useSwipeNavigation";
import { SwipeableScreen } from "../../common/components/swipeableScreen/SwipeableScreen";
import { useTheme } from "../../common/hooks/useTheme";
import styles from "./ItemScreen.module.scss";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import ImageGallery from "../../common/components/imageGallery/ImageGallery";
import { shortenText } from "../../common/utils/functions/shortenText";
import { SaleItemAttributes } from "../../common/components/saleItemAttributes/SaleItemAttributes";
import { ProductRatingSection } from "../../common/productRatingSection/ProductRatingSection";
import { StockDetails } from "../../common/stockDetails/StockDetails";
import { ItemRelations } from "../../common/components/itemRalations/ItemRelations";
import { CartSection } from "../../common/components/cartSection/CartSection";
import { Share } from "../../common/components/share/Share";
import { IdBadge } from "../../common/components/idBadge/IdBage";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../store/slices/storeListSlice";
import { ROUTES } from "../../common/utils/constants";
import { formatPriceToLocale } from "../../common/utils/functions/formatPriceToLocale";
import { useDisplayPrice } from "../../common/hooks/useDisplayPrice";
import { KitList } from "../../common/components/kitList/KitList";
import { RootState } from "../../store/store";
import { EntityType } from "../../common/types/enums";
import { Documents } from "../../common/components/documents/Documents";

export default function ItemScreen() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);
  const getPrice = useDisplayPrice();
  const isInComplexCart = useSelector((state: RootState) =>
    state.complexOrderCart.items.some((i) => i.itemId === Number(id)),
  );

  const {
    getCategoryName,
    openModal,
    handleFavoritePress,
    handleIncrease,
    handleDecrease,
    handleRemove,
    canRateItem,
    handleAddToCart,
    handleAddToComplex,
    quantity,
    currentItem,
    maxAvailable,
    isIncreaseDisabled,
    isOutOfStock,
    availability,
    isComplexCategory,
    showComplexButton,
    showCartButton,
  } = useItemScreen(Number(id));

  const { hasPrev, hasNext, prevItem, nextItem } = useSwipeNavigation(
    Number(id),
    currentItem?.categoryId ? Number(currentItem.categoryId) : undefined,
  );

  const currentUser = useSelector(selectCurrentUser);

  if (!currentItem) return null;

  const isFavorite = currentItem?.selected && currentItem.selected.length > 0;
  const hasUserRating = currentItem?.ratings && currentItem.ratings.length > 0;
  const displayPrice = getPrice(currentItem.prices);
  const hasPrice = Boolean(displayPrice);

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta
        itemName={currentItem.name}
        itemDescription={currentItem.description}
        itemImageFile={currentItem.files[0]}
        itemPrice={displayPrice}
        itemId={currentItem.id}
      />

      <ProductHeader
        title={currentItem.name}
        isFavorite={Boolean(isFavorite)}
        onFavoritePress={handleFavoritePress}
        showFavorite={currentItem.selected !== undefined && !!currentUser}
        textColor={theme.text}
        borderColor={theme.border}
        rightContent={<Share item={currentItem} />}
      />

      <SwipeableScreen
        hasPrev={hasPrev}
        hasNext={hasNext}
        prevPath={prevItem ? `${ROUTES.ITEM}/${prevItem.id}` : undefined}
        nextPath={nextItem ? `${ROUTES.ITEM}/${nextItem.id}` : undefined}
      >
        <div className={styles.mobileGallery}>
          <div className={styles.imageContainer}>
            <ImageGallery images={currentItem.files} />
          </div>
        </div>

        <div className={styles.scrollView}>
          <div className={styles.content}>
            <div className={styles.leftColumn}>
              <div className={styles.imageContainer}>
                <ImageGallery images={currentItem.files} />
                <IdBadge id={currentItem.id} />
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.nameRow}>
                <h1 className={styles.name}>{currentItem.name}</h1>
                <span className={styles.price}>
                  {hasPrice
                    ? `${formatPriceToLocale(displayPrice)} ₽`
                    : showComplexButton
                      ? ""
                      : "Нет цены"}
                </span>
              </div>

              <div className={styles.descriptionContainer}>
                <h3 className={styles.descriptionTitle}>Описание</h3>
                <p className={styles.descriptionText}>
                  {currentItem.description}
                </p>
              </div>

              {currentItem.items?.length > 0 && (
                <KitList
                  items={currentItem.items}
                  getCategoryName={getCategoryName}
                />
              )}

              <div className={styles.categoryContainer}>
                <span className={styles.categoryText}>
                  {shortenText(
                    getCategoryName(Number(currentItem.categoryId)),
                    30,
                  )}
                </span>
              </div>

              <div className={styles.attributesContainer}>
                <SaleItemAttributes attributes={currentItem.attributes} view />
              </div>

              <div className={styles.infoGrid}>
                <div className={styles.infoBlock}>
                  <ProductRatingSection
                    item={currentItem}
                    canRate={canRateItem}
                    hasUserRating={Boolean(hasUserRating)}
                    onRatePress={() => openModal(currentItem.id!)}
                    backgroundColor={theme.card}
                    textColor={theme.text}
                  />
                </div>

                <Documents
                  entityType={EntityType.ITEM}
                  entityId={Number(currentItem.id)}
                />

                <div className={styles.infoBlock}>
                  <StockDetails
                    stockStores={currentItem.stock_stores}
                    stockEnabled={currentItem.useStock}
                    forceStore={currentCluster?.forceStore}
                    itemStops={currentItem.item_stops}
                    selectedStoreIds={selectedStoreIds}
                    availabilityStatus={availability.status}
                  />
                </div>
              </div>

              <div className={styles.relationsWrapper}>
                <ItemRelations
                  addons={currentItem.addons}
                  recommendations={currentItem.recommendations}
                  getCategoryName={getCategoryName}
                />
              </div>

              <div className={styles.cartWrapper}>
                <CartSection
                  price={displayPrice}
                  unit={currentItem?.unit}
                  quantity={quantity}
                  onAdd={handleAddToCart}
                  onIncrease={handleIncrease}
                  onDecrease={handleDecrease}
                  onRemove={handleRemove}
                  layout="item"
                  isOutOfStock={isOutOfStock}
                  maxAvailable={maxAvailable}
                  isIncreaseDisabled={isIncreaseDisabled}
                  isComplexCategory={isComplexCategory}
                  showComplexButton={showComplexButton}
                  showCartButton={showCartButton}
                  onAddToComplex={handleAddToComplex}
                  isInComplexCart={isInComplexCart}
                />
              </div>
            </div>
          </div>
        </div>
      </SwipeableScreen>
    </div>
  );
}
